// WU-2.1 — /admin/announcements manager. Active table (body, level, audience,
// author, expiry, Clear) + recently-ended audit table (30d, from /manage).
// Server gates on supervisor.announce; the sidebar hides the entry too.
//
// SEMANTIC CORRECTION (Task 2 controller ruling): GET /announcements/manage
// returns three buckets — currently-active + cleared-within-30d +
// expired-but-uncleared-within-30d. Splitting on `clearedAt === null` alone
// would put expired-but-uncleared rows under "Active", which is wrong — split
// on the ACTIVE PREDICATE (not cleared AND not expired) instead. The second
// section is "Recently ended" because it holds both cleared AND expired rows.
import { useCallback, useEffect, useState } from "react";
import { isAnnouncementActive } from "@capture/shared";
import { announcementsApi, type AnnouncementRecord } from "../api/client";
import { ComposeAnnouncementDialog } from "../components/ComposeAnnouncementDialog";
import styles from "../announcements.module.css";

function audienceLabel(a: AnnouncementRecord): string {
  if (a.audienceType === "tenant") return "Everyone";
  if (a.audienceType === "supervisors") return "All supervisors";
  if (a.audienceType === "team") return `Team: ${a.teamName ?? a.teamId ?? "?"}`;
  return "One user";
}

function isActive(a: AnnouncementRecord, now: number): boolean {
  return isAnnouncementActive(a, now);
}

function endedLabel(a: AnnouncementRecord): string {
  if (a.clearedAt) return `Cleared ${new Date(a.clearedAt).toLocaleString()}`;
  if (a.expiresAt) return `Expired ${new Date(a.expiresAt).toLocaleString()}`;
  return "—";
}

export function AnnouncementsAdminPage() {
  const [rows, setRows] = useState<AnnouncementRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [composing, setComposing] = useState(false);

  const refetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await announcementsApi.listManage();
      setRows(res.announcements);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refetch(); }, [refetch]);

  const now = Date.now();
  const active = rows.filter((r) => isActive(r, now));
  const ended = rows.filter((r) => !isActive(r, now));

  const clear = async (id: string) => {
    try {
      await announcementsApi.clear(id);
    } catch (err) {
      // A failed clear (e.g. someone else already cleared it — 404) must not
      // strand the row: refetch runs in `finally` regardless, and the error
      // is swallowed here (no toast idiom on this page; the dialog above
      // uses inline `error` state, but a clear failure self-heals via
      // refetch so surfacing it isn't necessary).
      console.error("Failed to clear announcement", err);
    } finally {
      await refetch();
    }
  };

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <h1>Announcements</h1>
        <button type="button" onClick={() => setComposing(true)}>New Announcement</button>
      </header>
      {loading ? (
        <p>Loading…</p>
      ) : (
        <>
          <section>
            <h2>Active</h2>
            {active.length === 0 ? (
              <p className={styles.empty}>No active announcements.</p>
            ) : (
              <table className={styles.table}>
                <thead>
                  <tr><th>Message</th><th>Level</th><th>Audience</th><th>Posted by</th><th>Expires</th><th /></tr>
                </thead>
                <tbody>
                  {active.map((a) => (
                    <tr key={a.id}>
                      <td>{a.body}</td>
                      <td>{a.level}</td>
                      <td>{audienceLabel(a)}</td>
                      <td>{a.createdByName ?? a.createdBy}</td>
                      <td>{a.expiresAt ? new Date(a.expiresAt).toLocaleString() : "—"}</td>
                      <td><button type="button" onClick={() => void clear(a.id)}>Clear</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
          <section>
            <h2>Recently ended</h2>
            {ended.length === 0 ? (
              <p className={styles.empty}>Nothing cleared or expired in the last 30 days.</p>
            ) : (
              <table className={styles.table}>
                <thead>
                  <tr><th>Message</th><th>Audience</th><th>Posted by</th><th>Ended</th></tr>
                </thead>
                <tbody>
                  {ended.map((a) => (
                    <tr key={a.id}>
                      <td>{a.body}</td>
                      <td>{audienceLabel(a)}</td>
                      <td>{a.createdByName ?? a.createdBy}</td>
                      <td>{endedLabel(a)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </>
      )}
      {composing && (
        <ComposeAnnouncementDialog
          onClose={() => setComposing(false)}
          onCreated={() => { setComposing(false); void refetch(); }}
        />
      )}
    </div>
  );
}
